<template>
  <aside class="filters-form">
    <div class="control-group">
      <div class="control-label">{{ $res.Views.LocalHotelSearch_SearchByHotelName }}</div>
      <InputControl v-model="localSelection.name" @input="onChangeName" data-qa="hotel-name" />
    </div>
    <hr class="hr" style="margin: 13px 0 10px;">
    <div class="control-group control-block" data-qa="hotel-geography">
      <div class="control-label">{{ $res.General.Country }}</div>
      <SingleSelect
        v-model="localSelection.countryId"
        :placeholder="$res.General.AllCountries"
        :items="countries"
        :item-id-fn="idFn"
        :item-text-fn="textFn"
        @change="onChangeCountry"
        data-qa="country"
      >
        <template #selected-item="{ item }">
          <div class="country-element">
            <div v-if="idFn(item) > 0" class="icon-flag" :class="`icon-flag-${item.code}`"></div>
            <div>{{ textFn(item) }}</div>
          </div>
        </template>
        <template #element="{ item, isSelected }">
          <div class="country-element" :class="{ 'hotel-directory-form__item--any': !isSelected && item?.id === -1 }">
            <div v-if="idFn(item) > 0" class="icon-flag" :class="`icon-flag-${item.code}`"></div>
            <div>{{ textFn(item) }}</div>
          </div>
        </template>
      </SingleSelect>

      <div class="control-label">{{ $res.General.Region }}</div>
      <SingleSelect
        v-model="localSelection.regionId"
        :placeholder="$res.General.AllRegions"
        :disabled="localSelection.countryId < 0"
        :items="regions"
        :item-id-fn="idFn"
        :item-text-fn="textFn"
        @change="onChangeRegion"
        data-qa="region"
      />

      <div class="control-label">{{ $res.General.Location }}</div>
      <SingleSelect
        v-model="localSelection.locationId"
        :placeholder="$res.General.AllLocations"
        :disabled="localSelection.regionId < 0"
        :items="locations"
        :item-id-fn="idFn"
        :item-text-fn="textFn"
        @change="onChangeLocation"
        data-qa="location"
      />

      <div class="control-label">{{ $res.General.LocationArea }}</div>
      <SingleSelect
        v-model="localSelection.locationAreaId"
        :placeholder="$res.General.AllLocationAreas"
        :disabled="localSelection.locationId < 0"
        :items="locationAreas"
        :item-id-fn="idFn"
        :item-text-fn="textFn"
        @change="onChangeLocationArea"
        data-qa="area"
      >
        <template #element="{ item, isSelected }">
          <div class="hotel-directory-form__item" :class="{ 'hotel-directory-form__item--any': !isSelected && item?.id === -1 }">
            {{ textFn(item) }}
          </div>
        </template>
      </SingleSelect>
    </div>
    <div class="control-list" style="margin-top: 10px;" data-qa="hotel-category">
      <div class="control-list__header">
        <div>{{ $res.General.Category }}</div>
        <button v-show="localSelection.hotelCategoryGroupIds.length > 0" class="clear-button" type="button" @click="onClearCategory">
          {{ $res.General.ToReset }}
        </button>
      </div>
      <ul v-if="hotelCategoryGroups.length > 0" class="checkbox-list checkbox-list_2col">
        <li v-for="cat in hotelCategoryGroups" :key="cat.id">
          <CheckboxControl :value="cat.id" v-model="localSelection.hotelCategoryGroupIds" compact @change="onChangeCategory">
            {{ cat.name }}
          </CheckboxControl>
        </li>
      </ul>
      <div v-else>{{ $res.General.Any }}</div>
    </div>
    <div class="control-list" style="margin-top: 10px;" data-qa="hotel-attribute">
      <div class="control-list__header">
        <div>{{ $res.Views.LocalHotelSearch_TypeOfRest }}</div>
        <button v-show="localSelection.hotelAttributeIds.length > 0" class="clear-button" type="button" @click="onClearAttribute">
          {{ $res.General.ToReset }}
        </button>
      </div>
      <ul v-if="localHotelAttributes.length > 0" class="checkbox-list">
        <li v-for="attr in localHotelAttributes" :key="attr.id">
          <CheckboxControl :value="attr.id" v-model="localSelection.hotelAttributeIds" compact @change="onChangeAttribute">
            <SvgIcon v-if="attr.icon" :icon="attr.icon" class="icon-hotel-attribute" />
            <span>{{ attr.name }}</span>
          </CheckboxControl>
        </li>
      </ul>
      <div v-else>{{ $res.General.Any }}</div>
    </div>
    <hr class="hr" style="margin: 10px 0 8px;">
    <div class="control-group">
      <BaseButton size="max" variant="outline" @click="clearFilters">{{ $res.General.ClearFilters }}</BaseButton>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import InputControl from "@/common/controls/forms/input/InputControl.vue";
import SingleSelect from "@/common/controls/forms/single-select/SingleSelect.vue";
import BaseButton from "@/common/controls/BaseButton.vue";
import CheckboxControl from "@/common/controls/CheckboxControl.vue";
import SvgIcon from "@/common/controls/SvgIconControl.vue";
import { isKnownIcon } from "@/util/staticIcons";
import type { ActionType, HotelDirectorySelection } from "../types";

const props = defineProps({
  countries: { type: Array, default: () => [] },
  regions: { type: Array, default: () => [] },
  locations: { type: Array, default: () => [] },
  locationAreas: { type: Array, default: () => [] },
  hotelCategoryGroups: { type: Array, default: () => [] },
  hotelAttributes: { type: Array, default: () => [] },
  selection: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["selection-change"]);

const localSelection = reactive<HotelDirectorySelection>({
  name: props.selection.name ?? "",
  countryId: props.selection.countryId ?? -1,
  regionId: props.selection.regionId ?? -1,
  locationId: props.selection.locationId ?? -1,
  locationAreaId: props.selection.locationAreaId ?? -1,
  hotelCategoryGroupIds: [...(props.selection.hotelCategoryGroupIds ?? [])],
  hotelAttributeIds: [...(props.selection.hotelAttributeIds ?? [])],
});

const idFn = (x) => x.id;
const textFn = (x) => x.name;

const allCountriesText = window.Resources?.General?.AllCountries ?? "All countries";
const allRegionsText = window.Resources?.General?.AllRegions ?? "All regions";
const allLocationsText = window.Resources?.General?.AllLocations ?? "All locations";
const allLocationAreasText = window.Resources?.General?.AllLocationAreas ?? "All location areas";

const localHotelAttributes = computed(() => props.hotelAttributes.map((attr) => {
  const icon = `hotel-attribute_${(attr.code ?? "").toLocaleLowerCase()}`;
  return {
    ...attr,
    icon: isKnownIcon(icon) ? icon : null,
  };
}));

function selectionToPayload() {
  return {
    name: localSelection.name || null,
    countryId: localSelection.countryId === -1 ? null : localSelection.countryId,
    regionId: localSelection.regionId === -1 ? null : localSelection.regionId,
    locationId: localSelection.locationId === -1 ? null : localSelection.locationId,
    locationAreaId: localSelection.locationAreaId === -1 ? null : localSelection.locationAreaId,
    hotelCategoryGroupIds: [...localSelection.hotelCategoryGroupIds],
    hotelAttributeIds: [...localSelection.hotelAttributeIds],
  };
}

const countries = computed(() => [{ id: -1, name: allCountriesText }, ...[...props.countries].sort((a, b) => a.name.localeCompare(b.name))]);
const regions = computed(() => {
  if (localSelection.countryId > 0) {
    return [{ id: -1, name: allRegionsText }, ...props.regions.filter((x) => x.countryId === localSelection.countryId).sort((a, b) => a.name.localeCompare(b.name))];
  }
  return [{ id: -1, name: allRegionsText }];
});
const locations = computed(() => {
  if (localSelection.regionId > 0) {
    return [{ id: -1, name: allLocationsText }, ...props.locations.filter((x) => x.regionId === localSelection.regionId).sort((a, b) => a.name.localeCompare(b.name))];
  }
  return [{ id: -1, name: allLocationsText }];
});
const locationAreas = computed(() => {
  if (localSelection.locationId > 0) {
    return [{ id: -1, name: allLocationAreasText }, ...props.locationAreas.filter((x) => x.locationId === localSelection.locationId).sort((a, b) => a.name.localeCompare(b.name))];
  }
  return [{ id: -1, name: allLocationAreasText }];
});
const hotelCategoryGroups = computed(() => [...props.hotelCategoryGroups].sort((a, b) => b.name.localeCompare(a.name)));

function emitSelectionChange(action: ActionType) {
  emit("selection-change", {
    action,
    selection: selectionToPayload(),
  });
}

function clearFilters() {
  localSelection.name = "";
  localSelection.countryId = -1;
  localSelection.regionId = -1;
  localSelection.locationId = -1;
  localSelection.locationAreaId = -1;
  localSelection.hotelCategoryGroupIds = [];
  localSelection.hotelAttributeIds = [];
  emitSelectionChange("clear");
}

function onChangeName() { emitSelectionChange("name"); }
function onChangeCountry() {
  localSelection.regionId = -1;
  localSelection.locationId = -1;
  localSelection.locationAreaId = -1;
  emitSelectionChange("country");
}
function onChangeRegion() {
  localSelection.locationId = -1;
  localSelection.locationAreaId = -1;
  emitSelectionChange("region");
}
function onChangeLocation() {
  localSelection.locationAreaId = -1;
  emitSelectionChange("location");
}
function onChangeLocationArea() { emitSelectionChange("locationArea"); }
function onChangeCategory() { emitSelectionChange("category"); }
function onClearCategory() {
  localSelection.hotelCategoryGroupIds = [];
  onChangeCategory();
}
function onChangeAttribute() { emitSelectionChange("attribute"); }
function onClearAttribute() {
  localSelection.hotelAttributeIds = [];
  onChangeAttribute();
}

defineExpose({ clearFilters });
</script>

<style scoped lang="scss">
.filters-form {
  background-color: var(--white);
  padding: 15px 16px;
}

.hr {
  border-top: 2px solid var(--main-50);
}

.control-group {
  > * {
    width: 100%;
  }
}

.control-label {
  margin: 5px 0;

  &:first-of-type {
    margin-top: 0;
  }
}

.control-block {
  padding: 8px 14px 18px;
  border: 2px solid var(--main-50);
  border-radius: var(--border-radius-lg);
}

.control-list {
  padding: 8px 10px;
  border: 2px solid var(--main-50);
  border-radius: var(--border-radius-lg);
}

.clear-button {
  appearance: none;
  cursor: pointer;
  border: none;
  padding: 0;
  font-size: 11px;
  line-height: 16px;
  color: var(--main-10);
  transition: color 200ms ease;
  outline: none;
  background: transparent;

  &:hover {
    color: var(--hover);
  }
}

.checkbox-list {
  list-style-type: none;
  padding: 0;
  margin: 0;

  > * {
    padding: 2px 4px;
  }
}

.checkbox-list_2col {
  columns: 2;
  column-gap: 8px;

  > * {
    min-width: 0;
  }
}

.icon-hotel-attribute {
  flex: 0 0 auto;
  transform: translateY(2px);
  color: var(--main-10);
}

.control-list__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;

  > * {
    flex: 1 1 0;
    min-width: auto;
  }

  .clear-button {
    flex: 0 0 auto;
  }
}

.country-element {
  display: flex;
  gap: 4px;
  align-items: center;

  > * {
    flex: 1 1 0;
    min-width: 0;
  }

  > .icon-flag {
    flex: 0 0 auto;
  }
}

.hotel-directory-form__item--any {
  border-block-end: 2px solid var(--main-50);
}
</style>
